import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import parse from 'html-react-parser';
import CircularProgress from '@mui/material/CircularProgress';
import { City } from '../../../interfaces/config';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { Loading } from '../../../enums';
import { appActions } from '../../../redux/slices/appSlice';
import { __t } from '../../../utils/t';
import { highlightMatchingText } from '../../../utils';
import { ErrorMessage } from '../../Forms/ErrorMessage';
import { Icon } from '../../Icon';
import { CityAutocompleteProps } from '..';

import './styles.scss';

export const CityAutocompleteMobile = ({
    appType,
    options,
    selectedOption,
    errors,
    tabIndex,
    onSearch,
    onSelect,
    setSearchOptions,
}: CityAutocompleteProps) => {
    const dispatch = useAppDispatch();
    const { loading } = useAppSelector((state) => state.app);

    const [inputValue, setInputValue] = useState('');
    const [showMobileSearch, setShowMobileSearch] = useState(false);
    const isLoading = useMemo(() => loading === Loading.CityAutocomplete, [loading]);
    const inputRef = useRef<HTMLInputElement>(null);
    const searchContainerRef = useRef<HTMLDivElement>(null);
    const previewPlaceholderRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (selectedOption) {
            setInputValue(selectedOption.name);
        }

        return function () {
            setSearchOptions(null);
        };
    }, []);

    useEffect(() => {
        if (showMobileSearch && inputRef.current && searchContainerRef.current) {
            searchContainerRef.current.style.height = `${window.innerHeight}px`;
            inputRef.current.focus();
        }
    }, [showMobileSearch, inputRef, searchContainerRef]);

    const loader = useMemo(
        () => (isLoading ? <CircularProgress size={16} className='app-city-autocomplete-mobile__loader' /> : null),
        [isLoading],
    );

    const inputChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.trimStart();
        onSelect(null);
        setSearchOptions(null);
        setInputValue(value);
        if (value) {
            onSearch(value);
        }
    };

    const backClickHandler = () => {
        setShowMobileSearch(false);
        previewPlaceholderRef.current?.scrollIntoView();
        if (!selectedOption) {
            setInputValue('');
            setSearchOptions(null);
            dispatch(appActions.setLoading(null));
        }
    };

    const optionSelectHandler = (option: City) => {
        onSelect(option);
        setSearchOptions([option]);
        setInputValue(option.name);
        setShowMobileSearch(false);
        previewPlaceholderRef.current?.scrollIntoView();
    };

    const openMobileSearchHandler = () => {
        onSearch('');
        setShowMobileSearch(true);
    };

    const headerClassname = useMemo(() => {
        switch (appType) {
            case 'worker':
                return 'app-city-autocomplete-mobile__header--worker';
            case 'recruiter':
                return 'app-city-autocomplete-mobile__header--recruiter';
            case 'employer':
            default:
                return 'app-city-autocomplete-mobile__header--employer';
        }
    }, [appType]);

    const searchOptions = useMemo(() => {
        if (!options) {
            return null;
        }

        if (options && !options.length && !isLoading) {
            return (
                <ul className='app-city-autocomplete-mobile__options-list app-city-autocomplete-mobile__options-list--drop-up'>
                    <li className='app-city-autocomplete-mobile__option-item'>
                        <span>{__t('По вашему запросу ничего не найдено')}</span>
                    </li>
                </ul>
            );
        }

        return (
            <ul className='app-city-autocomplete-mobile__options-list'>
                {options.map((option) => (
                    <li
                        key={option.id}
                        className={`app-city-autocomplete-mobile__option-item ${
                            option.id === selectedOption?.id
                                ? 'app-city-autocomplete-mobile__option-item--selected'
                                : ''
                        }`}
                        onClick={() => optionSelectHandler(option)}
                    >
                        {parse(option.country.flag)}
                        {highlightMatchingText(option.name, inputValue)}
                        <span className='app-city-autocomplete-mobile__option-additional-text'>
                            {option.region ? `${option.region}, ` : ''}
                            {option.country.name}
                        </span>
                    </li>
                ))}
            </ul>
        );
    }, [options, selectedOption, inputValue, isLoading, showMobileSearch]);

    return (
        <>
            <div
                className={`app-city-autocomplete-mobile__preview ${
                    errors?.length ? 'app-city-autocomplete-mobile__preview--has-error' : ''
                }`}
                onClick={openMobileSearchHandler}
                ref={previewPlaceholderRef}
            >
                {inputValue ? (
                    inputValue
                ) : (
                    <span className='app-city-autocomplete-mobile__preview-placeholder'>
                        {__t('К примеру, Введите город')}
                    </span>
                )}
            </div>

            <ErrorMessage errors={errors} />

            {showMobileSearch && (
                <div className='app-city-autocomplete-mobile__container' ref={searchContainerRef}>
                    <div className={`app-city-autocomplete-mobile__header ${headerClassname}`}>
                        <div className='app-city-autocomplete-mobile__header-icon' onClick={backClickHandler}>
                            <Icon icon='chevron-left' color='white' size='20' />
                        </div>

                        <div className='app-city-autocomplete-mobile__input-container'>
                            <input
                                type='text'
                                value={inputValue}
                                onChange={inputChangeHandler}
                                className='app-city-autocomplete-mobile__input-component'
                                placeholder={__t('Введите город')}
                                ref={inputRef}
                                autoComplete='off'
                                tabIndex={tabIndex}
                            />
                            {loader}
                        </div>
                    </div>

                    {searchOptions}
                </div>
            )}
        </>
    );
};
